import { useEffect, useRef } from "react";
import type { InputHTMLAttributes } from "react";
import css from "./primitives.module.css";

type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** §7/§10: "/ from anywhere focuses it". */
  focusOnSlash?: boolean;
};

export function SearchInput({
  focusOnSlash = true,
  placeholder = "Search game title or arrangement title",
  ...rest
}: SearchInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focusOnSlash) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "/") return;
      const target = event.target as HTMLElement | null;
      // Don't steal the key from someone already typing.
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      // §7: "/ from anywhere focuses it" - and must not also type a slash.
      event.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusOnSlash]);

  return (
    <div className={css.searchWrap}>
      <span className={css.searchGlyph} aria-hidden />
      <input
        ref={ref}
        type="search"
        className={css.searchInput}
        placeholder={placeholder}
        {...rest}
      />
      <span className={css.keycap} aria-hidden>
        /
      </span>
    </div>
  );
}
